import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

declare global {
    interface Window {
        grecaptcha: {
            enterprise: {
                ready: (callback: () => void) => void;
                render: (
                    container: HTMLElement,
                    parameters: {
                        sitekey: string;
                        action?: string;
                        callback?: (token: string) => void;
                        "expired-callback"?: () => void;
                        "error-callback"?: () => void;
                    }
                ) => number;
                getResponse: (widgetId: number) => string;
                reset: (widgetId: number) => void;
            };
        };
    }
}

export interface ReCaptchaEnterpriseHandle {
    getValue: () => string | null;
    reset: () => void;
}

interface Props {
    sitekey: string;
    action?: string;
}

export const ReCaptchaEnterprise = forwardRef<ReCaptchaEnterpriseHandle, Props>(
    ({ sitekey, action }, ref) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const widgetIdRef = useRef<number | null>(null);

        useImperativeHandle(ref, () => ({
            getValue: () => {
                if (widgetIdRef.current !== null)
                    return window.grecaptcha?.enterprise?.getResponse(widgetIdRef.current) || null;
                return null;
            },
            reset: () => {
                if (widgetIdRef.current !== null)
                    window.grecaptcha?.enterprise?.reset(widgetIdRef.current);
            },
        }));

        useEffect(() => {
            const renderWidget = () => {
                if (!containerRef.current || widgetIdRef.current !== null) return;
                window.grecaptcha.enterprise.ready(() => {
                    if (!containerRef.current || widgetIdRef.current !== null) return;
                    widgetIdRef.current = window.grecaptcha.enterprise.render(containerRef.current, {
                        sitekey,
                        ...(action && { action }),
                    });
                });
            };

            if (window.grecaptcha?.enterprise) {
                renderWidget();
                return;
            }

            const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');
            if (existingScript) {
                existingScript.addEventListener("load", renderWidget);
                return () => existingScript.removeEventListener("load", renderWidget);
            }

            const script = document.createElement("script");
            script.src = "https://www.google.com/recaptcha/enterprise.js?render=explicit";
            script.async = true;
            script.defer = true;
            script.onload = renderWidget;
            document.head.appendChild(script);
        }, [sitekey, action]);

        return <div ref={containerRef} />;
    }
);
